import { useId, useState } from 'react';
import { codingAgentsSource } from './coding-agents-source';
import { useCodingAgentsCopy } from './coding-agents-copy';
import type { ConsentRequest, ConsentRequestStatus, DecisionResult } from './coding-agents-types';
import { consentView } from './consent-model';
import { Callout } from '@/components/ui/content';

const readStatus = (value: unknown): ConsentRequestStatus => {
  const answer = value as Partial<{ state: string; settled: string }> | null;
  if (answer?.state === 'applied' && (answer.settled === 'allow' || answer.settled === 'deny'))
    return { state: 'applied', settled: answer.settled };
  if (answer?.state === 'not_applied') return { state: 'not_applied' };
  if (answer?.state === 'pending') return { state: 'pending' };
  return { state: 'unknown' };
};

/**
 * The consent for one agent's request: every scope it asked for, grouped, with
 * what you can and can't grant in your current company. Allow sends exactly
 * your choice and the server applies current rights; this card never widens
 * anything and shows an out-of-date request as out of date.
 */
export function ConsentCard({
  request: initial,
  onDecided,
}: {
  request: ConsentRequest;
  onDecided?: (result: DecisionResult, decision: 'allow' | 'deny') => void;
}) {
  const {
    CONSENT_CLOSED,
    consentRecordedOther,
    CONSENT_STALE_ON_SEND,
    CONSENT_REFUSED_ON_SEND,
    consentTitle,
    notVerified,
    dataLine,
    expiryLine,
    refusalText,
    consentHeading,
    availabilityLabel,
    NOT_AVAILABLE_NOTE,
    consentNote,
    CONSENT_UNCONFIRMED,
    CONSENT_CHECK,
    CONSENT_STILL_PENDING,
    CONSENT_STILL_UNKNOWN,
    CONSENT_WORKING,
    CONSENT_ALLOW,
    CONSENT_DENY,
  } = useCodingAgentsCopy();
  const [request, setRequest] = useState(initial);
  const [seen, setSeen] = useState(initial);
  if (initial !== seen) {
    setSeen(initial);
    setRequest(initial);
  }
  const view = consentView(request);
  const heading = useId();
  const preDecision = view.state === 'requested' || view.state === 'partly_beyond_rights';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The choice that was sent and never confirmed. While it is set the card
  // offers no new decision: it first asks the server what became of it.
  const [unconfirmed, setUnconfirmed] = useState<'allow' | 'deny' | null>(null);
  // The server fenced the request: it cannot be decided any more.
  const [closed, setClosed] = useState(false);
  // The choice that was actually settled, when it is not the one this tab sent.
  const [recordedOther, setRecordedOther] = useState<'allow' | 'deny' | null>(null);

  // What the last status check said when it settled nothing.
  const [stillOpen, setStillOpen] = useState<null | 'pending' | 'unknown'>(null);

  // Only the server's word on this request lifts the lock: a reload showing
  // the request pending, revoked, stale or anything else settles nothing,
  // because the first decision may still be in flight.
  const reconcile = async (attempted: 'allow' | 'deny') => {
    setBusy(true);
    let answer: ConsentRequestStatus;
    try {
      answer = await codingAgentsSource().requestStatus(request.requestId);
    } catch {
      answer = { state: 'unknown' };
    }
    // Fail closed: anything that is not exactly one of the typed answers is unknown.
    answer = readStatus(answer);
    if (answer.state === 'applied') {
      let loaded: Awaited<ReturnType<ReturnType<typeof codingAgentsSource>['loadRequest']>>;
      try {
        loaded = await codingAgentsSource().loadRequest(request.requestId);
      } catch {
        loaded = { status: 'error' };
      }
      setBusy(false);
      // Applied, but the state to show could not be read: stay locked.
      if (loaded.status !== 'ready') return setStillOpen('unknown');
      setRequest(loaded.value);
      setUnconfirmed(null);
      setStillOpen(null);
      setError(null);
      // What was settled is what the server says, never what this tab sent.
      setRecordedOther(answer.settled === attempted ? null : answer.settled);
      onDecided?.('done', answer.settled);
      return;
    }
    setBusy(false);
    if (answer.state === 'not_applied') {
      // Fenced: this request is closed; a new one starts from the agent.
      setUnconfirmed(null);
      setStillOpen(null);
      setError(null);
      setClosed(true);
      return;
    }
    setStillOpen(answer.state);
  };

  // Exactly what the person saw and may grant: every shown scope within their
  // current rights. A consent restates the whole grant, so a scope already held
  // (knowledge:read is always required) is part of it; one beyond current
  // rights is not.
  const approvedScopeIds = view.rows
    .filter((row) => row.availability !== 'not_available')
    .map((row) => row.id);

  const decide = async (decision: 'allow' | 'deny') => {
    if (busy) return;
    setBusy(true);
    setError(null);
    let result: DecisionResult | 'lost' = 'lost';
    try {
      result = await codingAgentsSource().decide(
        request.requestId,
        decision,
        decision === 'allow' ? approvedScopeIds : [],
      );
    } catch {
      result = 'lost';
    }
    setBusy(false);
    // A thrown call or a failure after sending says nothing about whether the
    // server recorded the choice. Only stale and refused are the server's word.
    if (result === 'lost' || result === 'failed') {
      setUnconfirmed(decision);
      void reconcile(decision);
      return;
    }
    if (result === 'stale') setError(CONSENT_STALE_ON_SEND);
    else if (result === 'refused') setError(CONSENT_REFUSED_ON_SEND);
    onDecided?.(result, decision);
  };

  return (
    <section className="ws-consent" aria-labelledby={heading} data-state={view.state}>
      <h3 id={heading}>{consentTitle(request.companyName)}</h3>
      <p className="ws-consent-sub">
        {view.state === 'refused'
          ? refusalText(request.refusalReason)
          : notVerified(request.clientName, request.companyName, request.agentOrigin)}
      </p>
      {view.state !== 'refused' && (
        <>
          <h4>{consentHeading[view.state]}</h4>
          <ul className="ws-consent-list">
            {view.rows.map((row) => (
              <li key={row.id} data-availability={row.availability} data-family={row.family}>
                <strong>{row.label}</strong>
                <span>{row.description}</span>
                <em>{availabilityLabel[row.availability]}</em>
                {row.availability === 'not_available' && (
                  <small>{NOT_AVAILABLE_NOTE(request.companyName)}</small>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {preDecision && request.dataLeavesEu === true && request.vendorLabel && request.payerName && (
        <div data-line="data">
          <Callout tone="info">{dataLine(request.vendorLabel, request.payerName)}</Callout>
        </div>
      )}
      {preDecision && typeof request.lifetimeDays === 'number' && request.lifetimeDays > 0 && (
        <div data-line="expiry">
          <Callout tone="info">{expiryLine(request.lifetimeDays)}</Callout>
        </div>
      )}
      {consentNote[view.state] && <Callout tone="info">{consentNote[view.state]}</Callout>}
      <div role="status">
        {unconfirmed && (
          <div data-state="unconfirmed">
            <Callout
              tone="warning"
              actions={
                <button
                  type="button"
                  className="ws-text-button"
                  disabled={busy}
                  onClick={() => void reconcile(unconfirmed)}
                >
                  {CONSENT_CHECK}
                </button>
              }
            >
              {CONSENT_UNCONFIRMED}
              {stillOpen && (
                <p>{stillOpen === 'pending' ? CONSENT_STILL_PENDING : CONSENT_STILL_UNKNOWN}</p>
              )}
            </Callout>
          </div>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
        {closed && <Callout tone="warning">{CONSENT_CLOSED}</Callout>}
        {recordedOther && <Callout tone="info">{consentRecordedOther(recordedOther)}</Callout>}
      </div>
      {request.status === 'requested' && !unconfirmed && !closed && (
        <div className="ws-consent-actions">
          {view.canAllow && (
            <button
              type="button"
              className="ws-button primary"
              disabled={busy}
              onClick={() => void decide('allow')}
            >
              {busy ? CONSENT_WORKING : CONSENT_ALLOW}
            </button>
          )}
          <button
            type="button"
            className="ws-text-button"
            disabled={busy}
            onClick={() => void decide('deny')}
          >
            {CONSENT_DENY}
          </button>
        </div>
      )}
    </section>
  );
}
